import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ROUTE_QUERY_PARAM } from '../app/Routes/const';

export type DeepLinkParamConfig = {
  paramName: string;
  filtersKey?: string;
};

export type UseDeepLinkFilterOptions = {
  paramName?: string;
  filtersKey?: string;
  params?: DeepLinkParamConfig[];
  baseFilters: Record<string, any>;
};

export type UseDeepLinkFilterResult = {
  initialFilters: Record<string, any>;
  parseFiltersData: (filters: any) => Record<string, any> | null | undefined;
};

const normalizeParamNames = (
  options: UseDeepLinkFilterOptions
): DeepLinkParamConfig[] => {
  if (options.params?.length) return options.params;

  if (options.paramName) {
    return [
      {
        paramName: options.paramName,
        filtersKey: options.filtersKey ?? options.paramName,
      },
    ];
  }

  return [];
};

/**
 * URLSearchParams treats "+" as space (x-www-form-urlencoded). Phone deep links
 * often use a literal plus prefix (e.g. ?user_phone=+380...).
 */
const readQueryParam = (search: string, paramName: string): string | null => {
  const pattern = new RegExp(`[?&]${paramName}=([^&]*)`);
  const match = search.match(pattern);
  if (!match) return null;

  const raw = match[1];
  if (!raw) return null;

  let value = decodeURIComponent(raw.replace(/\+/g, '%2B'));

  if (
    paramName === ROUTE_QUERY_PARAM.USER_PHONE &&
    value.startsWith(' ') &&
    /^\s*\d/.test(value)
  ) {
    value = `+${value.trimStart()}`;
  }

  return value;
};

const collectDeepLinkValues = (
  search: string,
  configs: DeepLinkParamConfig[]
): Array<{ paramName: string; filtersKey: string; value: string }> => {
  const entries: Array<{
    paramName: string;
    filtersKey: string;
    value: string;
  }> = [];

  for (const { paramName, filtersKey = paramName } of configs) {
    const value = readQueryParam(search, paramName);
    if (value != null && value !== '') {
      entries.push({ paramName, filtersKey, value });
    }
  }

  return entries;
};

const applyDeepLinkToFilters = (
  base: Record<string, any>,
  entries: Array<{ filtersKey: string; value: string }>
): Record<string, any> =>
  entries.reduce(
    (filters, { filtersKey, value }) => ({ ...filters, [filtersKey]: value }),
    { ...base }
  );

export const useDeepLinkFilter = (
  options: UseDeepLinkFilterOptions
): UseDeepLinkFilterResult => {
  const history = useHistory();
  const location = useLocation();
  const paramConfigs = normalizeParamNames(options);

  const deepLinkEntries = collectDeepLinkValues(location.search, paramConfigs);

  const mountSnapshot = useRef({
    deepLinkEntries,
    baseFilters: options.baseFilters,
    search: location.search,
    pathname: location.pathname,
    history,
  });

  const initialFilters = applyDeepLinkToFilters(
    options.baseFilters,
    deepLinkEntries
  );

  const parseFiltersData = (
    filters: any
  ): Record<string, any> | null | undefined => {
    if (filters == null) return filters;
    if (!deepLinkEntries.length) return filters;

    return applyDeepLinkToFilters(filters, deepLinkEntries);
  };

  useEffect(() => {
    const {
      deepLinkEntries: entries,
      baseFilters: base,
      search,
      pathname,
      history: hist,
    } = mountSnapshot.current;

    if (!entries.length) return;

    const preserved = search
      .replace(/^\?/, '')
      .split('&')
      .filter(param => {
        const [key] = param.split('=');
        return (
          key &&
          !entries.some(entry => entry.paramName === decodeURIComponent(key))
        );
      })
      .join('&');

    const nextParams = new URLSearchParams(preserved ? `?${preserved}` : '');

    const existingFiltersStr = nextParams.get('filters');
    let existingFilters: Record<string, any> = {};

    if (existingFiltersStr) {
      try {
        existingFilters = JSON.parse(existingFiltersStr);
      } catch {
        // Malformed JSON — start from an empty object; deep-link values are
        // still written so the URL ends up with valid JSON.
      }
    } else {
      existingFilters = { ...base };
    }

    nextParams.set(
      'filters',
      JSON.stringify(applyDeepLinkToFilters(existingFilters, entries))
    );

    const nextSearch = nextParams.toString();
    hist.replace({
      pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  }, []);

  return { initialFilters, parseFiltersData };
};
